// ** React Imports
import { useRef } from "react";

// ** Third Party Components
import PropTypes from "prop-types";

// ** Component Components
import Text from "components/typography/Text";

// ** Utils & Helpers
import composeRefs from "utils/composeRefs";

// ** Styled Components
import {
	StyledCheckIcon,
	StyledHiddenCheckbox,
	StyledCheckboxControl,
	StyledCheckboxContainer,
	StyledLabelBaseComponent,
	StyledCheckboxContent,
} from "./styles";

// #####################################################

const Checkbox = ({
	renderLabel,
	showLabel,
	labelComponent,
	labelText,
	innerRef,
	disabled = false,
	noPaddingX,
	customPadding,
	customPaddingAllDevices,
	correctPosition,
	register,
	registerInEmail,
	hoverable = true,
	onChange,
	rowKey = null,
	withActiveBackgroundColor = false,
	label,
	...props
}) => {
	const checkboxRef = useRef(null);
	const showLabelFinal =
		showLabel || labelComponent || renderLabel || labelText;
	const renderContent = () => (
		<StyledCheckboxContainer
			onClick={(event) => {
				if (props.readOnly === true) {
					event.preventDefault();
				}
			}}
			correctPosition={correctPosition}
			$registerInEmail={registerInEmail}
		>
			<StyledHiddenCheckbox
				showLabel={showLabelFinal}
				labelText={labelText}
				ref={composeRefs(innerRef, checkboxRef)}
				disabled={disabled}
				onChange={(e) => {
					if (typeof onChange === "function") {
						onChange(e);
					}

					if (withActiveBackgroundColor && label === "Select all") {
						const allElements =
							document.querySelectorAll("[data-tableRowId]");

						if (props?.checked) {
							allElements.forEach((element) => {
								element.removeAttribute("data-rowActive");
							});
						} else {
							allElements.forEach((element) => {
								element.setAttribute("data-rowActive", "true");
							});
						}
					} else if (withActiveBackgroundColor && rowKey) {
						const element = document.querySelector(
							`[data-tableRowId="${rowKey}"]`,
						);
						if (element) {
							if (props?.checked) {
								element.removeAttribute("data-rowActive");
							} else {
								element.setAttribute("data-rowActive", "true");
							}
						}
					}
				}}
				{...props}
			/>
			<StyledCheckboxControl disabled={disabled}>
				<StyledCheckIcon />
			</StyledCheckboxControl>
		</StyledCheckboxContainer>
	);

	const renderLabelText = ({ fontSize }) => {
		if (renderLabel && typeof renderLabel === "function") {
			return renderLabel(labelText);
		}
		if (fontSize) {
			return (
				<Text size={fontSize} ml={2}>
					{labelText}
				</Text>
			);
		}
		return <Text ml={2}>{labelText}</Text>;
	};
	if (showLabelFinal) {
		if (labelComponent) {
			const LabelComponent = labelComponent;
			return (
				<LabelComponent
					as={StyledLabelBaseComponent}
					noPaddingX={noPaddingX}
					customPadding={customPadding}
					$customPaddingAllDevices={customPaddingAllDevices}
					hoverable={false}
					disabled={disabled}
					onClick={(event) => {
						if (props.readOnly === true) {
							event.preventDefault();
						}
					}}
					$registerInEmail={registerInEmail}
				>
					{renderContent()} {renderLabelText()}
				</LabelComponent>
			);
		}
		const LabelComponent = StyledLabelBaseComponent;
		return (
			<LabelComponent
				register={register}
				$registerInEmail={registerInEmail}
				disabled={disabled}
				noPaddingX={noPaddingX}
				onClick={(event) => {
					if (props.readOnly === true) {
						event.preventDefault();
					}
				}}
				hoverable={hoverable}
				customPadding={customPadding}
				$customPaddingAllDevices={customPaddingAllDevices}
			>
				{renderContent()}{" "}
				{renderLabelText({
					fontSize: registerInEmail && "0.9rem",
				})}
			</LabelComponent>
		);
	}
	return <label>{renderContent()}</label>;
};

// #####################################################

Checkbox.CheckboxControl = StyledCheckboxControl;
Checkbox.CheckboxContent = StyledCheckboxContent;

Checkbox.defaultProps = {
	showLabel: false,
};

Checkbox.propTypes = {
	showLabel: PropTypes.bool,
};

export default Checkbox;

// #####################################################
